import { GiftIcon, HistoryIcon, PackageIcon, SearchIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUser } from '@r/api/auth.tsx';
import { useQuery } from '@tanstack/react-query';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { useOrderColumns, useFilterFields } from '@r/components/home/order-columns.tsx';
import { DataTableAdvancedToolbar } from '@r/components/data-table/advanced/data-table-advanced-toolbar.tsx';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import type { ColumnFiltersState, ColumnSort, PaginationState } from '@tanstack/react-table';
import { type ReactNode, useEffect, useRef } from 'react';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { Card, CardContent } from '@ui/card.tsx';
import shopDefaultBanner from '@r/assets/images/fiddy/default-shop-banner.png';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import pointsTranslation from '@r/translations/points-translation.ts';
import { cn } from '@r/utils/utils.ts';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { Skeleton } from '@ui/skeleton.tsx';
import { Link } from '@tanstack/react-router';
import { Button } from '@ui/button.tsx';
import { apiOrdersGetCollection } from '@r/api/generated/client.ts';
import type {
  ApiOrdersGetCollectionParams,
  OrderOrderCollectionReadModel
} from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type Status = OrderOrderCollectionReadModel['status'];
type Sort = 'asc' | 'desc' | undefined;

type OrderFilters = {
  status: Status[] | undefined;
  createdAtSortValue: Sort;
  pointAmountSortValue: Sort;
};

export const Orders = () => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const user = useUser().data;
  const workspaces = UseWorkspace(user);

  async function ordersListFiltersQuery(
    filters: {
      status: Status[] | undefined;
      createdAtSortValue: Sort;
      pointAmountSortValue: Sort;
    },
    page: number | null = null,
    limit: number | null = null
  ) {
    const { status, createdAtSortValue, pointAmountSortValue } = filters;

    try {
      const params: ApiOrdersGetCollectionParams = {
        page: page ?? pageIndex + 1,
        perPage: limit ?? pageSize,
        ...(status && { 'status[]': status }),
        ...(createdAtSortValue && { 'order[createdAt]': createdAtSortValue }),
        ...(pointAmountSortValue && { 'order[point.amount]': pointAmountSortValue })
      };

      return await apiOrdersGetCollection(params);
    } catch (error) {
      if (isOrvalErrorFromPath(error, 'get', '/orders')) {
        showOrvalErrorToast(error);
      }
      throw error;
    }
  }

  function ordersListFilters(): OrderFilters {
    const statusFilter = columnFilters.find((filter: { id: string }) => filter.id === 'status');
    const status = statusFilter?.value as Status[] | undefined;

    const createdAtSort = sorting.find((sort: ColumnSort) => sort.id === 'createdAt');

    const createdAtSortValue: Sort = createdAtSort
      ? createdAtSort.desc
        ? 'desc'
        : 'asc'
      : undefined;

    const pointAmountSort = sorting.find((sort: ColumnSort) => sort.id === 'pointAmount');

    const pointAmountSortValue: Sort = pointAmountSort
      ? pointAmountSort.desc
        ? 'asc'
        : 'desc'
      : undefined;

    return { status, createdAtSortValue, pointAmountSortValue };
  }

  const {
    data: ordersPaginatedQuery,
    isPending: isOrdersPending,
    isError: isOrdersError,
    isSuccess: isOrdersSuccess,
    refetch: ordersRefetch
  } = useQuery({
    queryKey: ['order', workspaces.workspace?.id, user?.id],
    queryFn: async () => {
      const orders = await ordersListFiltersQuery(ordersListFilters());

      for (let i = 0; i < orders.data.length; i++) {
        (orders.data[i] as OrderOrderCollectionReadModel & { url: string }).url =
          `/orders/${orders.data[i].id}`;
      }

      return orders;
    },
    enabled: !!user
  });

  const {
    data: firstOrderPaginatedQuery,
    isPending: isFirstOrderPending,
    isError: isFirstOrderError,
    isSuccess: isFirstOrderSuccess
  } = useQuery({
    queryKey: ['order', 'first', workspaces.workspace?.id, user?.id],
    queryFn: () =>
      ordersListFiltersQuery(
        {
          status: undefined,
          createdAtSortValue: undefined,
          pointAmountSortValue: undefined
        },
        1,
        1
      ),
    enabled: !!user
  });

  const orders = ordersPaginatedQuery?.data ?? [];
  const firstOrder = firstOrderPaginatedQuery?.data?.[0];
  const filterFields = useFilterFields();
  const { table } = useDataTable({
    data: orders,
    columns: useOrderColumns(),
    filterFields,
    pageCount: ordersPaginatedQuery?.pagination?.last ?? 1,
    enableAdvancedFilter: true
  });

  const { pageIndex, pageSize }: PaginationState = table.getState().pagination as PaginationState;
  const sorting = table.getState().sorting;
  const columnFilters: ColumnFiltersState = table.getState().columnFilters;
  const prevValuesRef = useRef({ pageIndex, pageSize, columnFilters, sorting });

  useEffect(() => {
    const prevValues = prevValuesRef.current;

    if (
      pageIndex != prevValues.pageIndex ||
      pageSize != prevValues.pageSize ||
      JSON.stringify(columnFilters) !== JSON.stringify(prevValues.columnFilters) ||
      JSON.stringify(sorting) != JSON.stringify(prevValues.sorting)
    ) {
      ordersRefetch().then((r) => {
        if (r.isError) {
          console.error('Error fetching orders:', r.error);
        }
      });
    }

    prevValuesRef.current = { pageIndex, pageSize, columnFilters, sorting };
  }, [pageIndex, pageSize, columnFilters, sorting]);

  const statusStyles = {
    'order.status.pending': 'bg-warning/20 text-warning border-warning/30',
    'order.status.confirmed': 'bg-info/20 text-info border-info/30',
    'order.status.completed': 'bg-success/20 text-success border-success/30',
    'order.status.canceled': 'bg-muted text-muted-foreground border-muted-foreground/10',
    'order.status.failed': 'bg-destructive/20 text-destructive border-destructive/30'
  } as const;

  const OrderStatus = ({ status }: { status: Status }) => (
    <div
      className={cn(
        'inline-flex gap-2 justify-center items-center border',
        'px-2 py-1 text-sm font-medium whitespace-nowrap rounded-md',
        statusStyles[status]
      )}
    >
      {t(status)}
    </div>
  );

  const OrderInfosLine = (label: string, value: ReactNode) => (
    <p className="flex flex-wrap justify-center items-center text-sm whitespace-nowrap sm:justify-start sm:flex-nowrap">
      <span className="mr-3 text-muted-foreground">{t(label)}&nbsp;:</span>

      <span className="font-medium">{value}</span>
    </p>
  );

  return (
    <div className="overflow-x-hidden flex-grow">
      <div className="pl-8 flex flex-col space-y-10 h-full text-foreground">
        <span>
          <div className="flex flex-nowrap items-center mb-3">
            <PackageIcon className="mr-3" />

            <h1 className="flex items-center font-bold text-2xl md:text-3xl lg:text-[2.75rem] lg:leading-[3.5rem]">
              {t('menu.account.order.tracking')}
            </h1>
          </div>

          <p className="font-light text-muted-foreground">{t('order.tracking.description')}</p>
        </span>

        <div className="flex flex-col gap-10 h-full">
          {(isFirstOrderPending || isFirstOrderError) && (
            <section>
              <div className="flex items-center text-xl font-bold gap-2.5">
                <Skeleton className="w-6 h-6 bg-muted-foreground/10" />
                <Skeleton className="w-52 h-6 bg-muted-foreground/10" />
              </div>

              <Card className="p-6 mt-4 border-0">
                <div className="flex flex-col gap-5 justify-between p-0 md:flex-row lg:flex-col xl:flex-row">
                  <div className="flex flex-wrap gap-5 justify-center w-full sm:flex-nowrap sm:justify-start md:w-fit">
                    <div className="w-[150px] aspect-square sm:h-auto sm:max-w-[150px] flex justify-center">
                      <Skeleton className="w-full h-full sm:h-[150px] rounded-lg" />
                    </div>

                    <div className="flex flex-col justify-center space-y-3 w-full sm:w-auto">
                      <Skeleton className="w-24 h-3" />
                      <Skeleton className="w-48 h-4" />
                      <Skeleton className="w-40 h-4" />
                      <Skeleton className="w-36 h-4" />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-5 sm:justify-between md:flex-col md:items-end lg:flex-row lg:items-start xl:flex-col xl:items-end">
                    <Skeleton className="w-20 h-6" />
                    <Skeleton className="w-32 h-9" />
                  </div>
                </div>
              </Card>
            </section>
          )}

          {isFirstOrderSuccess && firstOrder && (
            <section className="space-y-2.5">
              <div className="flex items-center text-xl font-bold gap-2.5">
                <GiftIcon />
                <h2>{t('order.latest')}</h2>
              </div>

              <Card className="p-6 border-0">
                <CardContent className="flex flex-col gap-5 justify-between p-0 md:flex-row lg:flex-col xl:flex-row">
                  <div className="flex flex-wrap gap-5 justify-center w-full sm:flex-nowrap sm:justify-start md:w-fit">
                    <div className="w-full h-[100px] sm:h-auto sm:max-w-[150px] flex justify-center">
                      <img
                        src={firstOrder.shops[0]?.logo?.pathUri || shopDefaultBanner}
                        alt="shop logo"
                        className="object-cover object-center rounded-xl aspect-square"
                        onError={(e) => {
                          e.currentTarget.src = shopDefaultBanner;
                          e.currentTarget.onerror = null;
                        }}
                      />
                    </div>

                    <div className="flex flex-col justify-center space-y-2">
                      <p className="text-xs text-center text-muted-foreground/80 sm:text-left">
                        {t('order.reference.short')}
                        {firstOrder.reference}
                      </p>

                      {OrderInfosLine('order.supplier.label', firstOrder.shops[0]?.displayName)}

                      {OrderInfosLine(
                        'order.date.created',
                        format(new Date(firstOrder.createdAt), "d MMMM yyyy 'à' HH'h'mm", {
                          locale: fr
                        })
                      )}

                      {OrderInfosLine('order.items.count', firstOrder.productsCount)}

                      {OrderInfosLine(
                        'label.order.point.amount',
                        pointsTranslation(t, Math.abs(firstOrder.point?.amount ?? 0), true)
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-5 sm:justify-between md:flex-col md:items-end lg:flex-row lg:items-start xl:flex-col xl:items-end">
                    <div className="flex justify-center w-full sm:w-fit">
                      {OrderStatus({ status: firstOrder.status })}
                    </div>

                    <div className="flex justify-center w-full sm:w-fit">
                      <Link
                        to="/orders/$id"
                        params={{ id: firstOrder.id?.toString() ?? '' }}
                        className="inline-flex gap-2 items-center text-sm font-medium text-primary hover:underline"
                      >
                        <Button rounded="full">
                          <SearchIcon />
                          {t('order.details.view')}
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>
          )}

          <section className="flex flex-col grow">
            {(isOrdersPending || isOrdersError) && (
              <DataTableSkeleton
                columnCount={5}
                cellWidths={['10rem', '40rem', '12rem', '12rem', '8rem']}
                rowCount={6}
              />
            )}

            {isOrdersSuccess && (
              <>
                <DataTableAdvancedToolbar
                  table={table}
                  filterFields={filterFields}
                  title={t('order.history.title')}
                  titleIcon={<HistoryIcon />}
                />

                <DataTable
                  table={table}
                  totalItems={ordersPaginatedQuery?.pagination?.totalItems}
                  emptyIcon={PackageIcon}
                  emptyMessageKey="order.empty"
                  emptyFilteredMessageKey="order.filter.empty"
                />
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};
